import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-red-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-heading">Privacy Policy</CardTitle>
            <p className="text-sm text-gray-500">Last updated: November 27, 2024</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
              <p className="text-gray-700">
                Speak Algerian ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              <div className="text-gray-700 space-y-3">
                <div>
                  <p className="font-semibold">2.1 Personal Information:</p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Name and email address</li>
                    <li>Password (encrypted)</li>
                    <li>Learning goals and preferences</li>
                    <li>Progress and game scores</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold">2.2 Payment Information:</p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Payment information is processed securely through Stripe</li>
                    <li>We do not store credit card details on our servers</li>
                    <li>We receive transaction confirmations and subscription status from Stripe</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold">2.3 Usage Information:</p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Pages visited and features used</li>
                    <li>Time spent on the Service</li>
                    <li>Browser type and device information</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <div className="text-gray-700">
                <p className="mb-2">We use your information to:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Provide and maintain the Service</li>
                  <li>Process your subscription payments</li>
                  <li>Send you important updates and notifications</li>
                  <li>Track your learning progress</li>
                  <li>Improve our Service and user experience</li>
                  <li>Respond to your requests and support needs</li>
                  <li>Send promotional emails (you can opt-out anytime)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. How We Share Your Information</h2>
              <div className="text-gray-700 space-y-2">
                <p><strong>4.1 Service Providers:</strong> We share information with third-party service providers:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Stripe for payment processing</li>
                  <li>MongoDB Atlas for database hosting</li>
                  <li>Google for OAuth authentication (if you use Google sign-in)</li>
                </ul>
                <p><strong>4.2 Legal Requirements:</strong> We may disclose information if required by law or to protect our rights.</p>
                <p><strong>4.3 Business Transfers:</strong> If we are acquired or merged, your information may be transferred.</p>
                <p><strong>4.4 We Never:</strong> Sell your personal information to third parties.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security</h2>
              <p className="text-gray-700">
                We implement appropriate security measures to protect your information, including encryption of sensitive data, secure password storage, and regular security audits. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights (GDPR)</h2>
              <div className="text-gray-700">
                <p className="mb-2">If you are in the European Economic Area, you have the right to:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Request data portability</li>
                  <li>Withdraw consent at any time</li>
                </ul>
                <p className="mt-2">To exercise these rights, contact us at speakalgerian.contact@gmail.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Data Retention</h2>
              <p className="text-gray-700">
                We retain your information for as long as your account is active or as needed to provide services. If you delete your account, we will delete your personal information within 30 days, except where we are required to retain it for legal purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies and Tracking</h2>
              <p className="text-gray-700">
                We use cookies and similar tracking technologies to track activity on our Service and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Third-Party Links</h2>
              <p className="text-gray-700">
                Our Service may contain links to third-party websites (e.g., social media). We are not responsible for the privacy practices of these external sites. Please review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
              <p className="text-gray-700">
                Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to Privacy Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
              <p className="text-gray-700">
                If you have questions about this Privacy Policy or our data practices, please contact us:
                <br />
                Email: speakalgerian.contact@gmail.com
                <br />
                Instagram: @speakalgerian
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">13. International Data Transfers</h2>
              <p className="text-gray-700">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
